import React, { useEffect, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import "./OIDataAnalysis.css";
import { environment } from "environment/environment";
import useNiftyWebsocket from "service/websocket/NiftyWebsocketApi";
import usePageTitle from "component/common/PageTitle";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const INDEX_OPTIONS = [
  { label: "NIFTY", value: "NSE:NIFTY" },
  { label: "SENSEX", value: "BSE:SENSEX" },
];

const RANGE_OPTIONS = [
  { label: "ALL", value: "ALL" },
  { label: "ATM", value: "10" },
];

const PUT_COLOR = "#59b84d";
const CALL_COLOR = "#c96259";
const PUT_PATTERN_FILL = "rgba(89, 184, 77, 0.18)";
const CALL_PATTERN_FILL = "rgba(201, 98, 89, 0.18)";

const EMPTY_HEADER_STATS = {
  spotPrice: null,
  highestCallVolume: null,
  secondHighestCallVolume: null,
  highestPutVolume: null,
  secondHighestPutVolume: null,
  highestCallOi: null,
  secondHighestCallOi: null,
  highestPutOi: null,
  secondHighestPutOi: null,
  highestCallVolStrike: null,
  secondHighestCallVolStrike: null,
  highestCallOiStrike: null,
  secondHighestCallOiStrike: null,
  highestPutVolStrike: null,
  secondHighestPutVolStrike: null,
  highestPutOiStrike: null,
  secondHighestPutOiStrike: null,
  callTotalVolumeSum: 0,
  callTotalOISum: 0,
  callTotalOICHGSum: 0,
  putTotalVolumeSum: 0,
  putTotalOISum: 0,
  putTotalOICHGSum: 0,
};

const parseMaybeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumber = (value) => parseMaybeNumber(value) ?? 0;
const clampNonNegative = (value) => Math.max(0, toNumber(value));

const pickFirstFiniteNumber = (...values) => {
  for (const value of values) {
    const parsed = parseMaybeNumber(value);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

const isPointDifferenceWithinLimit = (value1, value2, limit = 10) => {
  const firstValue = parseMaybeNumber(value1);
  const secondValue = parseMaybeNumber(value2);

  if (firstValue == null || secondValue == null) {
    return false;
  }

  return Math.abs(firstValue - secondValue) <= limit;
};

const trimTrailingZeroes = (value) =>
  value.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");

const formatNumber = (value, digits = 0) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
};

const formatPrice = (value, digits = 1) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
};

const formatCompactIndianValue = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  const sign = parsed < 0 ? "-" : "";
  const absoluteValue = Math.abs(parsed);

  if (absoluteValue >= 10000000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 10000000).toFixed(absoluteValue >= 100000000 ? 0 : digits)
    )}Cr`;
  }

  if (absoluteValue >= 100000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 100000).toFixed(absoluteValue >= 1000000 ? 0 : digits)
    )}L`;
  }

  if (absoluteValue >= 1000) {
    return `${sign}${new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(absoluteValue)}`;
  }

  return `${sign}${trimTrailingZeroes(absoluteValue.toFixed(digits))}`;
};

const formatAxisValue = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  const sign = parsed < 0 ? "-" : "";
  const absoluteValue = Math.abs(parsed);

  if (absoluteValue >= 10000000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 10000000).toFixed(absoluteValue % 10000000 === 0 ? 0 : 1)
    )}Cr`;
  }

  if (absoluteValue >= 100000) {
    return `${sign}${trimTrailingZeroes(
      (absoluteValue / 100000).toFixed(absoluteValue % 100000 === 0 ? 0 : 1)
    )}L`;
  }

  return `${sign}${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(absoluteValue)}`;
};

const formatRatio = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null || !Number.isFinite(parsed)) {
    return "--";
  }

  return parsed.toFixed(2);
};

const formatPlainNumber = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return parsed.toFixed(digits);
};

const formatPercentValue = (value, digits = 2) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "--";
  }

  return `${trimTrailingZeroes(parsed.toFixed(digits))}%`;
};

const getLtpRangeTierClass = (value) => {
  const parsed = parseMaybeNumber(value);

  if (parsed == null) {
    return "";
  }

  if (parsed >= 90) {
    return "oi-ltp-range-tier-blue";
  }

  if (parsed >= 80) {
    return "oi-ltp-range-tier-yellow";
  }

  if (parsed >= 70) {
    return "oi-ltp-range-tier-pink";
  }

  return "";
};

const getLtpRangePercentage = (ltpValue, lowValue, highValue) => {
  const ltp = parseMaybeNumber(ltpValue);
  const low = parseMaybeNumber(lowValue);
  const high = parseMaybeNumber(highValue);

  if (ltp == null || low == null || high == null) {
    return null;
  }

  if (high === low) {
    return ltp >= high ? 100 : 0;
  }

  return ((ltp - low) / (high - low)) * 100;
};

const formatSpotSymbolName = (value) => {
  const rawValue = typeof value === "string" ? value.trim() : "";

  if (!rawValue) {
    return "SPOT";
  }

  const symbolWithoutExchange = rawValue.includes(":") ? rawValue.split(":").pop() || rawValue : rawValue;
  const normalizedSymbol = symbolWithoutExchange.replace(/-INDEX$/i, "-SPOT");

  if (/^NIFTY$/i.test(normalizedSymbol)) {
    return "NIFTY50-SPOT";
  }

  if (/^SENSEX$/i.test(normalizedSymbol)) {
    return "SENSEX-SPOT";
  }

  return normalizedSymbol;
};

const formatChartDate = (value) => {
  const candidates = [value, value ? String(value).replace(" ", "T") : null];
  let resolvedDate = null;

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const parsedDate = new Date(candidate);

    if (!Number.isNaN(parsedDate.getTime())) {
      resolvedDate = parsedDate;
      break;
    }
  }

  if (!resolvedDate) {
    resolvedDate = new Date();
  }

  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(resolvedDate);
};

const sortByStrike = (rows = []) =>
  [...rows].sort((firstRow, secondRow) => {
    return toNumber(firstRow.strikePrice) - toNumber(secondRow.strikePrice);
  });

const filterOptionRows = (rows, spotPrice, rangeKey) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const sideCount = Number(rangeKey);
  const validSpotPrice = parseMaybeNumber(spotPrice);

  if (!Number.isFinite(sideCount) || sideCount <= 0 || validSpotPrice == null) {
    return rows;
  }

  const closestIndex = rows.reduce((closestRowIndex, row, rowIndex) => {
    const currentDifference = Math.abs(toNumber(row.strikePrice) - validSpotPrice);
    const closestDifference = Math.abs(
      toNumber(rows[closestRowIndex].strikePrice) - validSpotPrice
    );

    return currentDifference < closestDifference ? rowIndex : closestRowIndex;
  }, 0);

  const startIndex = Math.max(0, closestIndex - sideCount);
  const endIndex = Math.min(rows.length - 1, closestIndex + sideCount);

  return rows.slice(startIndex, endIndex + 1);
};

const parseStructuredList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return [];
    }

    try {
      return parseStructuredList(JSON.parse(trimmedValue));
    } catch {
      return [];
    }
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value.data)) {
      return value.data;
    }

    if (Array.isArray(value.list)) {
      return value.list;
    }

    if (Array.isArray(value.rows)) {
      return value.rows;
    }
  }

  return [];
};

const resolveNestedListSource = (marketData, rows, key) => {
  if (marketData?.[key] != null) {
    return marketData[key];
  }

  if (!Array.isArray(rows)) {
    return null;
  }

  return rows.find((row) => row?.[key] != null)?.[key] ?? null;
};

const extractMinuteVolumeLabel = (entry, fallbackIndex) => {
  if (typeof entry === "string") {
    return entry.trim() || `Minute ${fallbackIndex + 1}`;
  }

  if (Array.isArray(entry)) {
    const textValue = entry.find((value) => typeof value === "string" && value.trim());
    return textValue?.trim() || `Minute ${fallbackIndex + 1}`;
  }

  if (!entry || typeof entry !== "object") {
    return `Minute ${fallbackIndex + 1}`;
  }

  const directLabel = [
    entry.minute,
    entry.minuteLabel,
    entry.minuteRange,
    entry.timeRange,
    entry.label,
    entry.interval,
    entry.slot,
    entry.timeSlot,
    entry.bucketLabel,
    entry.bucket,
    entry.range,
  ].find((value) => typeof value === "string" && value.trim());

  if (directLabel) {
    return directLabel.trim();
  }

  const startValue = [
    entry.startTime,
    entry.fromTime,
    entry.start,
    entry.startMinute,
  ].find((value) => typeof value === "string" && value.trim());
  const endValue = [
    entry.endTime,
    entry.toTime,
    entry.end,
    entry.endMinute,
  ].find((value) => typeof value === "string" && value.trim());

  if (startValue && endValue) {
    return `${startValue.trim()} - ${endValue.trim()}`;
  }

  return `Minute ${fallbackIndex + 1}`;
};

const parseMinuteLabelSortValue = (label) => {
  if (typeof label !== "string" || !label.trim()) {
    return null;
  }

  const matchedTime = label.match(/(\d{1,2}):(\d{2})/);
  if (!matchedTime) {
    return null;
  }

  const hours = parseMaybeNumber(matchedTime[1]);
  const minutes = parseMaybeNumber(matchedTime[2]);
  if (hours == null || minutes == null) {
    return null;
  }

  return (hours * 60) + minutes;
};

const extractMinuteVolumeValue = (entry, side) => {
  if (typeof entry === "number") {
    return entry;
  }

  if (Array.isArray(entry)) {
    const lastNumericValue = [...entry]
      .reverse()
      .find((value) => parseMaybeNumber(value) != null);

    return parseMaybeNumber(lastNumericValue);
  }

  if (!entry || typeof entry !== "object") {
    return parseMaybeNumber(entry);
  }

  return pickFirstFiniteNumber(
    entry.currentVolume,
    entry.startVolume,
    side === "call" ? entry.totalCallVolume : entry.totalPutVolume,
    side === "call" ? entry.callVolume : entry.putVolume,
    side === "call" ? entry.ceVolume : entry.peVolume,
    entry.totalVolume,
    entry.volume,
    entry.value,
    entry.total,
    entry.amount,
    entry.y
  );
};

const extractMinuteBarVolume = (entry, side) => {
  if (typeof entry === "number") {
    return entry;
  }

  if (Array.isArray(entry)) {
    return extractMinuteVolumeValue(entry, side);
  }

  if (!entry || typeof entry !== "object") {
    return parseMaybeNumber(entry);
  }

  return pickFirstFiniteNumber(
    entry.minuteVolume,
    entry.oneMinuteVolume,
    entry.volumeChange,
    entry.currentVolume != null && entry.startVolume != null
      ? toNumber(entry.currentVolume) - toNumber(entry.startVolume)
      : null,
    side === "call" ? entry.totalCallVolume : entry.totalPutVolume,
    side === "call" ? entry.callVolume : entry.putVolume,
    side === "call" ? entry.ceVolume : entry.peVolume,
    entry.totalVolume,
    entry.volume,
    entry.value,
    entry.total,
    entry.amount,
    entry.y
  );
};

const extractMinuteVolumeRank = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  return parseMaybeNumber(entry.rank ?? entry.position ?? entry.order);
};

const buildMinuteVolumePanelData = (rawList, side) => {
  const parsedEntries = parseStructuredList(rawList)
    .map((entry, index) => ({
      minuteLabel: extractMinuteVolumeLabel(entry, index),
      rank: extractMinuteVolumeRank(entry),
      sourceIndex: index,
      volume: extractMinuteVolumeValue(entry, side),
      barVolume: extractMinuteBarVolume(entry, side),
    }))
    .map((entry) => ({
      ...entry,
      minuteSortValue: parseMinuteLabelSortValue(entry.minuteLabel),
    }))
    .filter((entry) => entry.volume != null || entry.barVolume != null);

  if (parsedEntries.length === 0) {
    return {
      rows: [],
      maxVolume: 0,
      totalVolume: 0,
    };
  }

  const hasExplicitRank = parsedEntries.some((entry) => entry.rank != null);
  const hasMinuteSortValue = parsedEntries.some((entry) => entry.minuteSortValue != null);
  const orderedEntries = hasMinuteSortValue
    ? [...parsedEntries].sort((firstEntry, secondEntry) => {
      const sortDifference = toNumber(secondEntry.minuteSortValue) - toNumber(firstEntry.minuteSortValue);
      if (sortDifference !== 0) {
        return sortDifference;
      }

      if (hasExplicitRank) {
        const rankDifference = toNumber(firstEntry.rank) - toNumber(secondEntry.rank);
        if (rankDifference !== 0) {
          return rankDifference;
        }
      }

      return toNumber(firstEntry.sourceIndex) - toNumber(secondEntry.sourceIndex);
    })
    : hasExplicitRank
      ? [...parsedEntries].sort((firstEntry, secondEntry) => {
        return toNumber(firstEntry.rank) - toNumber(secondEntry.rank);
      })
      : [...parsedEntries];

  let runningTotalVolume = 0;
  const rows = orderedEntries.slice(0, 10).map((entry, index) => {
    const barVolume = entry.barVolume ?? entry.volume;
    runningTotalVolume += toNumber(barVolume);

    return {
      ...entry,
      rank: index + 1,
      barVolume,
      totalVolume: runningTotalVolume,
    };
  });

  return {
    rows,
    maxVolume: rows.reduce((maximumValue, entry) => {
      return Math.max(maximumValue, toNumber(entry.barVolume));
    }, 0),
    totalVolume: runningTotalVolume,
  };
};

const buildMetrics = (rows = []) => {
  const callOiTotal = rows.reduce((sum, row) => sum + toNumber(row.ceOI), 0);
  const putOiTotal = rows.reduce((sum, row) => sum + toNumber(row.peOI), 0);
  const callOiChangeTotal = rows.reduce((sum, row) => sum + toNumber(row.ceOIChg), 0);
  const putOiChangeTotal = rows.reduce((sum, row) => sum + toNumber(row.peOIChg), 0);
  const callVolumeTotal = rows.reduce((sum, row) => sum + toNumber(row.ceVolume), 0);
  const putVolumeTotal = rows.reduce((sum, row) => sum + toNumber(row.peVolume), 0);

  return {
    callOiTotal,
    putOiTotal,
    callOiChangeTotal,
    putOiChangeTotal,
    callVolumeTotal,
    putVolumeTotal,
    pcrOi: callOiTotal !== 0 ? putOiTotal / callOiTotal : null,
    pcrOiChange: callOiChangeTotal !== 0 ? putOiChangeTotal / callOiChangeTotal : null,
    pcrVolume: callVolumeTotal !== 0 ? putVolumeTotal / callVolumeTotal : null,
  };
};

const buildHeaderStats = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return EMPTY_HEADER_STATS;
  }

  const sortDesc = (values = []) => [...new Set(values)].sort((firstValue, secondValue) => {
    return secondValue - firstValue;
  });

  const extract = (key) =>
    rows.map((row) => parseMaybeNumber(row[key])).filter((value) => value != null);

  const findStrike = (key, targetValue) => {
    if (targetValue == null) {
      return null;
    }

    return rows.find((row) => parseMaybeNumber(row[key]) === targetValue)?.strikePrice ?? null;
  };

  const sortedCallVolume = sortDesc(extract("ceVolume"));
  const sortedPutVolume = sortDesc(extract("peVolume"));
  const sortedCallOi = sortDesc(extract("ceOI"));
  const sortedPutOi = sortDesc(extract("peOI"));
  const sortedCallOiChange = sortDesc(extract("ceOIChg"));
  const sortedPutOiChange = sortDesc(extract("peOIChg"));

  return {
    spotPrice: pickFirstFiniteNumber(rows[0]?.spotPrice, rows[0]?.niftySpotPrice),
    highestCallVolume: sortedCallVolume[0] ?? null,
    secondHighestCallVolume: sortedCallVolume[1] ?? null,
    highestPutVolume: sortedPutVolume[0] ?? null,
    secondHighestPutVolume: sortedPutVolume[1] ?? null,
    highestCallOi: sortedCallOi[0] ?? null,
    secondHighestCallOi: sortedCallOi[1] ?? null,
    highestPutOi: sortedPutOi[0] ?? null,
    secondHighestPutOi: sortedPutOi[1] ?? null,
    highestCallVolStrike: findStrike("ceVolume", sortedCallVolume[0]),
    secondHighestCallVolStrike: findStrike("ceVolume", sortedCallVolume[1]),
    highestCallOiStrike: findStrike("ceOI", sortedCallOi[0]),
    secondHighestCallOiStrike: findStrike("ceOI", sortedCallOi[1]),
    highestPutVolStrike: findStrike("peVolume", sortedPutVolume[0]),
    secondHighestPutVolStrike: findStrike("peVolume", sortedPutVolume[1]),
    highestPutOiStrike: findStrike("peOI", sortedPutOi[0]),
    secondHighestPutOiStrike: findStrike("peOI", sortedPutOi[1]),
    callTotalVolumeSum: sortedCallVolume.reduce((sum, value) => sum + value, 0),
    callTotalOISum: sortedCallOi.reduce((sum, value) => sum + value, 0),
    callTotalOICHGSum: sortedCallOiChange.reduce((sum, value) => sum + value, 0),
    putTotalVolumeSum: sortedPutVolume.reduce((sum, value) => sum + value, 0),
    putTotalOISum: sortedPutOi.reduce((sum, value) => sum + value, 0),
    putTotalOICHGSum: sortedPutOiChange.reduce((sum, value) => sum + value, 0),
  };
};

const getPercentage = (value, highest) => (highest ? ((value / highest) * 100).toFixed(2) : "0");

const getBarPercentageValue = (value, highest) => {
  const safeValue = clampNonNegative(value);
  const safeHighest = clampNonNegative(highest);

  if (!safeHighest || !safeValue) {
    return 0;
  }

  return Math.round((safeValue / safeHighest) * 100);
};

const formatBarPercentageLabel = (value, highest) => {
  return `${getBarPercentageValue(value, highest)}%`;
};

const getVolumeBadgeVariant = (value, highest, secondHighest) => {
  const safeValue = clampNonNegative(value);
  const safeHighest = clampNonNegative(highest);
  const safeSecondHighest = clampNonNegative(secondHighest);

  if (!safeValue || !safeHighest) {
    return "default";
  }

  const percentage = getBarPercentageValue(safeValue, safeHighest);

  if (safeValue === safeHighest) {
    return "strong";
  }

  if (safeValue === safeSecondHighest && percentage >= 75) {
    return "warning";
  }

  if (percentage >= 75 && safeValue < (safeSecondHighest || Number.POSITIVE_INFINITY)) {
    return "bold";
  }

  return "default";
};

const getChartThemeRoot = (chart) => {
  if (typeof document === "undefined") {
    return null;
  }

  return chart?.canvas?.closest(".oi-visual-page") || document.documentElement;
};

const getChartCssVar = (chart, variableName, fallbackValue) => {
  const themeRoot = getChartThemeRoot(chart);

  if (!themeRoot || typeof window === "undefined") {
    return fallbackValue;
  }

  const resolvedValue = window.getComputedStyle(themeRoot).getPropertyValue(variableName).trim();
  return resolvedValue || fallbackValue;
};

const getChartCssNumberVar = (chart, variableName, fallbackValue) => {
  const resolvedValue = Number.parseFloat(getChartCssVar(chart, variableName, String(fallbackValue)));
  return Number.isFinite(resolvedValue) ? resolvedValue : fallbackValue;
};

const getVolumeLabelTheme = (chart) => {
  const fontWeight = getChartCssVar(chart, "--oi-volume-badge-font-weight", "800");
  const boldFontWeight = getChartCssVar(chart, "--oi-volume-badge-bold-font-weight", "900");
  const fontSize = getChartCssVar(chart, "--oi-volume-badge-font-size", "11px");
  const fontFamily = getChartCssVar(chart, "--oi-volume-badge-font-family", '"Segoe UI", sans-serif');

  return {
    textColor: getChartCssVar(chart, "--oi-volume-badge-text", "#0f172a"),
    defaultBackground: getChartCssVar(chart, "--oi-volume-badge-default-bg", "#9bd34f"),
    defaultBorder: getChartCssVar(chart, "--oi-volume-badge-default-border", "#9bd34f"),
    strongBackground: getChartCssVar(chart, "--oi-volume-badge-strong-bg", "rgb(56, 190, 235)"),
    strongBorder: getChartCssVar(chart, "--oi-volume-badge-strong-border", "rgb(56, 190, 235)"),
    warningBackground: getChartCssVar(chart, "--oi-volume-badge-warning-bg", "yellow"),
    warningBorder: getChartCssVar(chart, "--oi-volume-badge-warning-border", "#facc15"),
    font: `${fontWeight} ${fontSize} ${fontFamily}`,
    boldFont: `${boldFontWeight} ${fontSize} ${fontFamily}`,
    horizontalPadding: getChartCssNumberVar(chart, "--oi-volume-badge-padding-x", 10),
    height: getChartCssNumberVar(chart, "--oi-volume-badge-height", 26),
    radius: getChartCssNumberVar(chart, "--oi-volume-badge-radius", 9),
    offset: getChartCssNumberVar(chart, "--oi-volume-badge-offset", 8),
  };
};

const getVolumeLabelBadgeStyle = (theme, variant, strokeColor) => {
  if (variant === "strong") {
    return {
      background: theme.strongBackground,
      border: theme.strongBorder,
      text: theme.textColor,
      font: theme.font,
    };
  }

  if (variant === "warning") {
    return {
      background: theme.warningBackground,
      border: theme.warningBorder,
      text: theme.textColor,
      font: theme.font,
    };
  }

  if (variant === "bold") {
    return {
      background: theme.defaultBackground,
      border: theme.defaultBorder || strokeColor,
      text: theme.textColor,
      font: theme.boldFont,
    };
  }

  return {
    background: theme.defaultBackground,
    border: theme.defaultBorder || strokeColor,
    text: theme.textColor,
    font: theme.font,
  };
};

const getCallResistanceLogic = (highestCallVolStrike, highestCallOiStrike, spotPrice) => {
  if (highestCallVolStrike == null || highestCallOiStrike == null || spotPrice == null) {
    return null;
  }

  if (highestCallVolStrike === highestCallOiStrike) {
    return highestCallVolStrike;
  }

  const volumeDistance = Math.abs(highestCallVolStrike - spotPrice);
  const oiDistance = Math.abs(highestCallOiStrike - spotPrice);

  return volumeDistance < oiDistance ? highestCallVolStrike : highestCallOiStrike;
};

const getCallResistanceWttAndWtb = (
  callResistance,
  secondHighestCallVolStrike,
  secondHighestCallVolume,
  highestCallVolume
) => {
  if (
    callResistance == null ||
    secondHighestCallVolStrike == null ||
    secondHighestCallVolume == null ||
    highestCallVolume == null
  ) {
    return "N/A";
  }

  const percentage = `${getPercentage(secondHighestCallVolume, highestCallVolume)}%`;
  const percentageValue = parseFloat(percentage);

  if (secondHighestCallVolStrike < callResistance) {
    return percentageValue >= 75 ? `WTB - ${percentage}` : "STRNG";
  }

  return percentageValue >= 75 ? `WTT - ${percentage}` : "STRNG";
};

const getPutSupportLogic = (highestPutVolStrike, highestPutOiStrike, spotPrice) => {
  if (highestPutVolStrike == null || highestPutOiStrike == null || spotPrice == null) {
    return null;
  }

  if (highestPutVolStrike === highestPutOiStrike) {
    return highestPutVolStrike;
  }

  const volumeDistance = Math.abs(highestPutVolStrike - spotPrice);
  const oiDistance = Math.abs(highestPutOiStrike - spotPrice);

  return volumeDistance < oiDistance ? highestPutVolStrike : highestPutOiStrike;
};

const getPutSupportWttAndWtb = (
  secondHighestPutVolStrike,
  secondHighestPutVolume,
  putSupport,
  highestPutVolume
) => {
  if (
    putSupport == null ||
    secondHighestPutVolStrike == null ||
    secondHighestPutVolume == null ||
    highestPutVolume == null
  ) {
    return "N/A";
  }

  const percentage = `${getPercentage(secondHighestPutVolume, highestPutVolume)}%`;
  const percentageValue = parseFloat(percentage);

  if (secondHighestPutVolStrike < putSupport) {
    return percentageValue >= 75 ? `WTB - ${percentage}` : "STRNG";
  }

  return percentageValue >= 75 ? `WTT - ${percentage}` : "STRNG";
};

const getBuySellSignal = (stats) => {
  if (!stats) {
    return "NO_SIGNAL";
  }

  const {
    callTotalOICHGSum,
    putTotalOICHGSum,
    callTotalOISum,
    putTotalOISum,
    callTotalVolumeSum,
    putTotalVolumeSum,
  } = stats;

  if (
    callTotalOICHGSum > putTotalOICHGSum &&
    callTotalOISum > putTotalOISum &&
    callTotalVolumeSum < putTotalVolumeSum
  ) {
    return "SELL_ON_RISE";
  }

  if (
    putTotalOICHGSum > callTotalOICHGSum &&
    putTotalOISum > callTotalOISum &&
    putTotalVolumeSum < callTotalVolumeSum
  ) {
    return "BUY_ON_DEEP";
  }

  return "NO_SIGNAL";
};

const getStrikeStep = (rows = []) => {
  const strikeDifferences = rows
    .slice(1)
    .map((row, index) => Math.abs(toNumber(row.strikePrice) - toNumber(rows[index].strikePrice)))
    .filter((difference) => difference > 0);

  if (!strikeDifferences.length) {
    return 50;
  }

  return Math.min(...strikeDifferences);
};

const buildOiSegments = (oiValue, oiChangeValue) => {
  const currentOi = clampNonNegative(oiValue);
  const oiChange = toNumber(oiChangeValue);

  if (oiChange >= 0) {
    const increase = Math.min(oiChange, currentOi);

    return {
      base: Math.max(currentOi - increase, 0),
      increase,
      decrease: 0,
      currentOi,
      changeMagnitude: increase,
      hasDecrease: false,
    };
  }

  return {
    base: currentOi,
    increase: 0,
    decrease: Math.abs(oiChange),
    currentOi,
    changeMagnitude: Math.abs(oiChange),
    hasDecrease: true,
  };
};

const getStripePattern = (chart, strokeColor, fillColor) => {
  if (!chart?.ctx || typeof document === "undefined") {
    return fillColor;
  }

  const cacheKey = `${strokeColor}-${fillColor}`;

  if (!chart.$oiPatternCache) {
    chart.$oiPatternCache = {};
  }

  if (!chart.$oiPatternCache[cacheKey]) {
    const patternCanvas = document.createElement("canvas");
    patternCanvas.width = 12;
    patternCanvas.height = 12;

    const patternContext = patternCanvas.getContext("2d");

    if (!patternContext) {
      return fillColor;
    }

    patternContext.clearRect(0, 0, 12, 12);
    patternContext.fillStyle = fillColor;
    patternContext.fillRect(0, 0, 12, 12);
    patternContext.strokeStyle = strokeColor;
    patternContext.lineWidth = 2;

    patternContext.beginPath();
    patternContext.moveTo(-2, 10);
    patternContext.lineTo(5, 3);
    patternContext.moveTo(2, 14);
    patternContext.lineTo(9, 7);
    patternContext.moveTo(6, 6);
    patternContext.lineTo(13, -1);
    patternContext.stroke();

    chart.$oiPatternCache[cacheKey] = chart.ctx.createPattern(patternCanvas, "repeat") || fillColor;
  }

  return chart.$oiPatternCache[cacheKey];
};

const roundRect = (ctx, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
};

const spotMarkerPlugin = {
  id: "spotMarker",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;

    if (!pluginOptions?.enabled || !chartArea || !xScale) {
      return;
    }

    const spotValue = parseMaybeNumber(pluginOptions.spotValue);

    if (spotValue == null) {
      return;
    }

    const xCoordinate = xScale.getPixelForValue(spotValue);

    if (
      !Number.isFinite(xCoordinate) ||
      xCoordinate < chartArea.left ||
      xCoordinate > chartArea.right
    ) {
      return;
    }

    const label = pluginOptions.label || "";
    const labelHeight = 30;
    const labelY = chartArea.top + 4;

    ctx.save();
    ctx.strokeStyle = pluginOptions.lineColor || "rgba(15, 23, 42, 0.65)";
    ctx.lineWidth = 1.25;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(xCoordinate, label ? labelY + labelHeight + 4 : chartArea.top);
    ctx.lineTo(xCoordinate, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);

    if (label) {
      const horizontalPadding = 12;
      const labelRadius = 12;

      ctx.font = "600 13px 'Segoe UI', sans-serif";

      const textWidth = ctx.measureText(label).width;
      const boxWidth = textWidth + horizontalPadding * 2;
      const boxX = Math.min(
        Math.max(xCoordinate - boxWidth / 2, chartArea.left + 4),
        chartArea.right - boxWidth - 4
      );

      ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
      ctx.strokeStyle = "rgba(15, 23, 42, 0.08)";
      ctx.lineWidth = 1;

      roundRect(ctx, boxX, labelY, boxWidth, labelHeight, labelRadius);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#172033";
      ctx.textBaseline = "middle";
      ctx.fillText(label, boxX + horizontalPadding, labelY + labelHeight / 2);
    }

    ctx.restore();
  },
};

const volumePercentageLabelPlugin = {
  id: "volumePercentageLabel",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    if (!pluginOptions?.enabled) {
      return;
    }

    const { ctx, chartArea } = chart;

    if (!ctx || !chartArea) {
      return;
    }

    const volumeLabelTheme = getVolumeLabelTheme(chart);

    ctx.save();

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!dataset?.showBarTopLabels) {
        return;
      }

      const meta = chart.getDatasetMeta(datasetIndex);
      const valueLabels = Array.isArray(dataset.valueLabels) ? dataset.valueLabels : [];
      const valuePercentages = Array.isArray(dataset.valuePercentages)
        ? dataset.valuePercentages
        : [];
      const valueVariants = Array.isArray(dataset.valueVariants)
        ? dataset.valueVariants
        : [];
      const strokeColor =
        typeof dataset.borderColor === "string" ? dataset.borderColor : "#172033";

      meta.data.forEach((barElement, valueIndex) => {
        const label = valueLabels[valueIndex];
        const percentage = Number(valuePercentages[valueIndex] ?? 0);
        const variant = valueVariants[valueIndex] || "default";
        const yValue = Number(dataset.data?.[valueIndex]?.y ?? 0);

        if (!label || !Number.isFinite(yValue) || yValue <= 0) {
          return;
        }

        const { x, y, base } = barElement;

        if (![x, y, base].every(Number.isFinite)) {
          return;
        }

        const badgeStyle = getVolumeLabelBadgeStyle(volumeLabelTheme, variant, strokeColor);
        const verticalOffset = pluginOptions.labelOffset ?? volumeLabelTheme.offset;

        ctx.font = badgeStyle.font || volumeLabelTheme.font;

        const textWidth = ctx.measureText(label).width;
        const horizontalPadding = volumeLabelTheme.horizontalPadding;
        const boxWidth = textWidth + horizontalPadding * 2;
        const boxHeight = volumeLabelTheme.height;
        const boxX = Math.min(
          Math.max(x - boxWidth / 2, chartArea.left + 4),
          chartArea.right - boxWidth - 4
        );
        const boxY = Math.max(y - boxHeight - verticalOffset, 4);

        ctx.fillStyle = badgeStyle.background;
        ctx.strokeStyle = badgeStyle.border;
        ctx.lineWidth = 1;
        roundRect(ctx, boxX, boxY, boxWidth, boxHeight, volumeLabelTheme.radius);
        ctx.fill();
        if (badgeStyle.border !== badgeStyle.background) {
          ctx.stroke();
        }

        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = badgeStyle.text;
        ctx.fillText(label, boxX + boxWidth / 2, boxY + boxHeight / 2);
      });
    });

    ctx.restore();
  },
};

export default function OIDataAnalysis() {
  const { options } = useNiftyWebsocket(environment.niftyWebsocketUrl);

  const [indexDayRangeData, setIndexDayRangeData] = useState(null);
  const [indexSymbolName, setIndexSymbolName] = useState("NIFTY");
  const [allOptionRows, setAllOptionRows] = useState([]);
  const [visibleOptionRows, setVisibleOptionRows] = useState([]);
  const [selectedStrikeFilter, setSelectedStrikeFilter] = useState("10");
  const [indexFilterValueChanges, setIndexFilterValueChangeFilter] = useState("NSE:NIFTY");
  const [activeView, setActiveView] = useState("optionOi");
  const [showOiChange, setShowOiChange] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [blinkSignal, setBlinkSignal] = useState(true);
  const marketData = options?.[indexFilterValueChanges] ?? null;

  useEffect(() => {
    if (!marketData) {
      setIndexDayRangeData(null);
      setAllOptionRows([]);
      setVisibleOptionRows([]);
      return;
    }

    setIndexDayRangeData(marketData.indexDayRange || null);

    const sortedRows = Array.isArray(marketData.optionDataModelList)
      ? sortByStrike(marketData.optionDataModelList)
      : [];

    const marketSpotPrice = pickFirstFiniteNumber(
      marketData?.indexLiveDataModel?.lp,
      sortedRows[0]?.spotPrice,
      marketData?.indexDayRange?.niftySpotPrice
    );

    setIndexSymbolName(
      formatSpotSymbolName(
        sortedRows[0]?.indexSymbol || indexFilterValueChanges.split(":")[1] || "NIFTY"
      )
    );
    setAllOptionRows(sortedRows);
    setVisibleOptionRows(filterOptionRows(sortedRows, marketSpotPrice, selectedStrikeFilter));
  }, [marketData, indexFilterValueChanges, selectedStrikeFilter]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setBlinkSignal((previousValue) => !previousValue);
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  usePageTitle(indexDayRangeData?.niftySpotPrice ?? "N/A", indexDayRangeData?.expiryDate ?? "N/A");

  const currentSpotPrice = pickFirstFiniteNumber(
    options?.[indexFilterValueChanges]?.indexLiveDataModel?.lp,
    indexDayRangeData?.niftySpotPrice,
    allOptionRows[0]?.spotPrice
  );

  const previousDayHigh = parseMaybeNumber(indexDayRangeData?.previousDayHigh);
  const previousDayLow = parseMaybeNumber(indexDayRangeData?.previousDayLow);
  const previousDayClose = parseMaybeNumber(indexDayRangeData?.previousDayClose);
  const dayHigh = parseMaybeNumber(indexDayRangeData?.dayHigh);
  const dayLow = parseMaybeNumber(indexDayRangeData?.dayLow);

  const isNearPDH =
    previousDayHigh != null &&
    currentSpotPrice != null &&
    Math.abs(previousDayHigh - currentSpotPrice) <= 20;
  const isNearPDL =
    previousDayLow != null &&
    currentSpotPrice != null &&
    Math.abs(previousDayLow - currentSpotPrice) <= 20;
  const isNearPDC =
    previousDayClose != null &&
    currentSpotPrice != null &&
    Math.abs(previousDayClose - currentSpotPrice) <= 20;
  const isNearDH =
    dayHigh != null &&
    currentSpotPrice != null &&
    Math.abs(dayHigh - currentSpotPrice) <= 20;
  const isNearDL =
    dayLow != null &&
    currentSpotPrice != null &&
    Math.abs(dayLow - currentSpotPrice) <= 20;

  const indiaVixValue = pickFirstFiniteNumber(
    options?.["NSE:INDIAVIX"]?.indexLiveDataModel?.lp,
    options?.["NSE:INDIA VIX"]?.indexLiveDataModel?.lp,
    indexDayRangeData?.indiaVix
  );

  const visibleStats = buildHeaderStats(visibleOptionRows);
  const callResistance = getCallResistanceLogic(
    visibleStats.highestCallVolStrike,
    visibleStats.highestCallOiStrike,
    visibleStats.spotPrice ?? currentSpotPrice
  );
  const putSupport = getPutSupportLogic(
    visibleStats.highestPutVolStrike,
    visibleStats.highestPutOiStrike,
    visibleStats.spotPrice ?? currentSpotPrice
  );
  const marketSignal = getBuySellSignal(visibleStats);

  const renderBuySellSignal = () => {
    if (marketSignal === "SELL_ON_RISE") {
      return (
        <span className={`header-signal sell-rise ${blinkSignal ? "blink" : ""}`}>
          SELL ON RISE
        </span>
      );
    }

    if (marketSignal === "BUY_ON_DEEP") {
      return (
        <span className={`header-signal buy-deep ${blinkSignal ? "blink" : ""}`}>
          BUY ON DEEP
        </span>
      );
    }

    return <span className="header-signal neutral">NO SIGNAL</span>;
  };

  const headerData = [
    {
      label1: "Top-1",
      value1: formatPlainNumber(indexDayRangeData?.dayTopOne, 2),
      label2: "Top-2",
      value2: formatPlainNumber(indexDayRangeData?.dayTopTwo, 2),
    },
    {
      label1: "Bottom-1",
      value1: formatPlainNumber(indexDayRangeData?.dayBottomOne, 2),
      label2: "Bottom-2",
      value2: formatPlainNumber(indexDayRangeData?.dayBottomTwo, 2),
    },
    {
      value1: renderBuySellSignal(),
    },
    {
      label1: "RES",
      value1: `${formatPlainNumber(callResistance, 0)} - ${getCallResistanceWttAndWtb(
        callResistance,
        visibleStats.secondHighestCallVolStrike,
        visibleStats.secondHighestCallVolume,
        visibleStats.highestCallVolume
      )}`,
      label2: "SUP",
      value2: `${formatPlainNumber(putSupport, 0)} - ${getPutSupportWttAndWtb(
        visibleStats.secondHighestPutVolStrike,
        visibleStats.secondHighestPutVolume,
        putSupport,
        visibleStats.highestPutVolume
      )}`,
    },
    {
      label1: "PDH",
      value1: formatPlainNumber(indexDayRangeData?.previousDayHigh, 2),
      label2: "PDL",
      value2: formatPlainNumber(indexDayRangeData?.previousDayLow, 2),
      highlight: isNearPDH,
      highlight2: isNearPDL,
    },
    {
      label1: "PDC",
      value1: formatPlainNumber(indexDayRangeData?.previousDayClose, 2),
      label2: "OPEN",
      value2: formatPlainNumber(indexDayRangeData?.niftyOpenPrice, 2),
      highlight: isNearPDC,
    },
    {
      label1: "DH",
      value1: formatPlainNumber(indexDayRangeData?.dayHigh, 2),
      label2: "DL",
      value2: formatPlainNumber(indexDayRangeData?.dayLow, 2),
      highlight: isNearDH,
      highlight2: isNearDL,
    },
    {
      label1: "ExpDate",
      value1: indexDayRangeData?.expiryDate || "--",
      label2: "DateTime",
      value2: indexDayRangeData?.currentDateTime || "--",
    },
  ];
  const leftHeaderData = headerData.filter((item) => item.label1 !== "ExpDate");
  const rightHeaderData = headerData.find((item) => item.label1 === "ExpDate") || null;

  const allMetrics = buildMetrics(allOptionRows);
  const visibleMetrics = buildMetrics(visibleOptionRows);
  const topCallMinuteVolumeData = buildMinuteVolumePanelData(
    resolveNestedListSource(marketData, allOptionRows, "ce10MVolumeDataList"),
    "call"
  );
  const topPutMinuteVolumeData = buildMinuteVolumePanelData(
    resolveNestedListSource(marketData, allOptionRows, "pe10MVolumeDataList"),
    "put"
  );
  const minuteVolumePanels = [
    {
      key: "call",
      cardClassName: "is-call",
      title: "CALL VOLUME",
      totalLabel: "TOTAL CALL VOLUME (10 MINUTE)",
      barLabel: "CALL VOLUME BAR",
      data: topCallMinuteVolumeData,
    },
    {
      key: "put",
      cardClassName: "is-put",
      title: "PUT VOLUME",
      totalLabel: "TOTAL PUT VOLUME (10 MINUTE)",
      barLabel: "PUT VOLUME BAR",
      data: topPutMinuteVolumeData,
    },
  ];
  const strikeStep = getStrikeStep(visibleOptionRows);
  const chartDateLabel = formatChartDate(indexDayRangeData?.currentDateTime);
  const isOptionOiView = activeView === "optionOi";
  const isOptionOiChangeView = activeView === "optionOiChange";
  const isVolumeView = activeView === "volume";
  const isLtpAnalysisView = activeView === "ltpAnalysis";
  const showOiOverlay = isOptionOiView && showOiChange;
  const cardTitle = isOptionOiChangeView
    ? `OI Change on ${chartDateLabel}`
    : isVolumeView
      ? `Volume on ${chartDateLabel}`
      : isLtpAnalysisView
        ? `LTP Analysis on ${chartDateLabel}`
        : `Open Interest on ${chartDateLabel}`;

  const chartRows = visibleOptionRows
    .map((row) => {
      const strikePrice = parseMaybeNumber(row.strikePrice);

      if (strikePrice == null) {
        return null;
      }

      return {
        strikePrice,
        putOi: clampNonNegative(row.peOI),
        putOiChange: toNumber(row.peOIChg),
        putVolume: clampNonNegative(row.peVolume),
        putVolumePercentageValue: getBarPercentageValue(row.peVolume, visibleStats.highestPutVolume),
        putVolumePercentageLabel: formatBarPercentageLabel(row.peVolume, visibleStats.highestPutVolume),
        putVolumeBadgeVariant: getVolumeBadgeVariant(
          row.peVolume,
          visibleStats.highestPutVolume,
          visibleStats.secondHighestPutVolume
        ),
        callOi: clampNonNegative(row.ceOI),
        callOiChange: toNumber(row.ceOIChg),
        callVolume: clampNonNegative(row.ceVolume),
        callVolumePercentageValue: getBarPercentageValue(
          row.ceVolume,
          visibleStats.highestCallVolume
        ),
        callVolumePercentageLabel: formatBarPercentageLabel(
          row.ceVolume,
          visibleStats.highestCallVolume
        ),
        callVolumeBadgeVariant: getVolumeBadgeVariant(
          row.ceVolume,
          visibleStats.highestCallVolume,
          visibleStats.secondHighestCallVolume
        ),
        ...buildOiSegments(row.peOI, row.peOIChg),
        callSegments: buildOiSegments(row.ceOI, row.ceOIChg),
      };
    })
    .filter(Boolean);

  const ltpAnalysisRows = visibleOptionRows
    .map((row) => {
      const strikePrice = parseMaybeNumber(row.strikePrice);

      if (strikePrice == null) {
        return null;
      }

      return {
        strikePrice,
        callMaxLow: parseMaybeNumber(row.ceMaxLow),
        callMaxHigh: parseMaybeNumber(row.ceMaxHigh),
        callLtp: parseMaybeNumber(row.ceLtp),
        callRangePercentage: pickFirstFiniteNumber(
          row.ceMaxHighLowPercentage,
          getLtpRangePercentage(row.ceLtp, row.ceMaxLow, row.ceMaxHigh)
        ),
        putLtp: parseMaybeNumber(row.peLtp),
        putRangePercentage: pickFirstFiniteNumber(
          row.peMaxHighLowPercentage,
          getLtpRangePercentage(row.peLtp, row.peMaxLow, row.peMaxHigh)
        ),
        putMaxHigh: parseMaybeNumber(row.peMaxHigh),
        putMaxLow: parseMaybeNumber(row.peMaxLow),
      };
    })
    .filter(Boolean)
    .sort((firstRow, secondRow) => firstRow.strikePrice - secondRow.strikePrice);
  const ltpClosestStrike = ltpAnalysisRows.reduce((closestStrike, row) => {
    if (currentSpotPrice == null) {
      return closestStrike;
    }

    if (closestStrike == null) {
      return row.strikePrice;
    }

    return Math.abs(row.strikePrice - currentSpotPrice) < Math.abs(closestStrike - currentSpotPrice)
      ? row.strikePrice
      : closestStrike;
  }, null);

  const overallPcr = allMetrics.pcrOi;
  const activePcr = isVolumeView ? allMetrics.pcrVolume : overallPcr;
  const visiblePcr = isVolumeView ? visibleMetrics.pcrVolume : visibleMetrics.pcrOi;
  const visiblePutLabel = isVolumeView ? "Total Put Volume" : "Total Put OI";
  const visibleCallLabel = isVolumeView ? "Total Call Volume" : "Total Call OI";
  const visiblePutValue = isVolumeView ? visibleMetrics.putVolumeTotal : visibleMetrics.putOiTotal;
  const visibleCallValue = isVolumeView ? visibleMetrics.callVolumeTotal : visibleMetrics.callOiTotal;
  const volumePutColor = CALL_COLOR;
  const volumeCallColor = PUT_COLOR;
  const chartSurfaceWidth = chartRows.length > 14 ? `${chartRows.length * 74}px` : "100%";
  const helperText =
    isOptionOiView
      ? showOiOverlay
        ? "Solid green and red bars show the live put and call OI. Hatched overlays mark OI increase and hollow overlays mark OI decrease for each strike. The dashed line marks the live spot price."
        : "Green bars show put open interest and red bars show call open interest for the visible strike range. The dashed line marks the live spot price."
      : isVolumeView
        ? "Red bars show put volume and green bars show call volume for the visible strike range. The dashed line marks the live spot price."
        : isLtpAnalysisView
          ? "The LTP analysis table shows call-side max low, max high, max-high-low percentage, and CE LTP on the left, with strike in the middle and PE LTP plus matching put-side values on the right."
          : "Green bars show put OI change and red bars show call OI change. Positive bars rise above zero and negative bars fall below zero for each strike. The dashed line marks the live spot price.";

  const chartData = isOptionOiView
    ? showOiOverlay
      ? {
        datasets: [
          {
            label: "Put OI",
            tooltipLabel: "Put OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.base })),
            backgroundColor: PUT_COLOR,
            borderColor: PUT_COLOR,
            stack: "put",
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Increase",
            tooltipLabel: "Put Increase",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.increase })),
            backgroundColor: (context) => getStripePattern(context.chart, PUT_COLOR, PUT_PATTERN_FILL),
            borderColor: PUT_COLOR,
            stack: "put",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
          {
            label: "Decrease",
            tooltipLabel: "Put Decrease",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.decrease })),
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            borderColor: PUT_COLOR,
            stack: "put",
            borderWidth: 1.3,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
          {
            label: "Call OI",
            tooltipLabel: "Call OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callSegments.base })),
            backgroundColor: CALL_COLOR,
            borderColor: CALL_COLOR,
            stack: "call",
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Increase",
            tooltipLabel: "Call Increase",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callSegments.increase })),
            backgroundColor: (context) =>
              getStripePattern(context.chart, CALL_COLOR, CALL_PATTERN_FILL),
            borderColor: CALL_COLOR,
            stack: "call",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
          {
            label: "Decrease",
            tooltipLabel: "Call Decrease",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callSegments.decrease })),
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            borderColor: CALL_COLOR,
            stack: "call",
            borderWidth: 1.3,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
          },
        ],
      }
      : {
        datasets: [
          {
            label: "Put OI",
            tooltipLabel: "Put OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.putOi })),
            backgroundColor: PUT_COLOR,
            borderColor: PUT_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Call OI",
            tooltipLabel: "Call OI",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callOi })),
            backgroundColor: CALL_COLOR,
            borderColor: CALL_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
        ],
      }
    : isVolumeView
      ? {
        datasets: [
          {
            label: "Put Volume",
            tooltipLabel: "Put Volume",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.putVolume })),
            valuePercentages: chartRows.map((row) => row.putVolumePercentageValue),
            valueLabels: chartRows.map((row) => row.putVolumePercentageLabel),
            valueVariants: chartRows.map((row) => row.putVolumeBadgeVariant),
            showBarTopLabels: true,
            backgroundColor: volumePutColor,
            borderColor: volumePutColor,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Call Volume",
            tooltipLabel: "Call Volume",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callVolume })),
            valuePercentages: chartRows.map((row) => row.callVolumePercentageValue),
            valueLabels: chartRows.map((row) => row.callVolumePercentageLabel),
            valueVariants: chartRows.map((row) => row.callVolumeBadgeVariant),
            showBarTopLabels: true,
            backgroundColor: volumeCallColor,
            borderColor: volumeCallColor,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
        ],
      }
      : {
        datasets: [
          {
            label: "Put OI chg",
            tooltipLabel: "Put OI change",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.putOiChange })),
            backgroundColor: PUT_COLOR,
            borderColor: PUT_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
          {
            label: "Call OI chg",
            tooltipLabel: "Call OI change",
            data: chartRows.map((row) => ({ x: row.strikePrice, y: row.callOiChange })),
            backgroundColor: CALL_COLOR,
            borderColor: CALL_COLOR,
            borderRadius: 4,
            borderSkipped: false,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
            maxBarThickness: 22,
            minBarLength: chartRows.length ? 2 : 0,
          },
        ],
      };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        top: 42,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 14,
          boxHeight: 14,
          padding: 22,
          color: "#364152",
          font: {
            size: 13,
            weight: "500",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.94)",
        titleColor: "#ffffff",
        bodyColor: "#e5e7eb",
        borderColor: "rgba(148, 163, 184, 0.25)",
        borderWidth: 1,
        displayColors: true,
        filter: (tooltipItem) =>
          isOptionOiChangeView ? Number(tooltipItem.raw?.y) !== 0 : Number(tooltipItem.raw?.y) > 0,
        callbacks: {
          title: (items) => (items.length ? `Strike ${formatNumber(items[0].parsed.x, 0)}` : ""),
          label: (context) =>
            `${context.dataset.tooltipLabel || context.dataset.label}: ${formatCompactIndianValue(
              context.parsed.y
            )}`,
        },
      },
      spotMarker: {
        enabled: currentSpotPrice != null,
        spotValue: currentSpotPrice,
        label:
          currentSpotPrice != null
            ? `${indexSymbolName} ${formatPrice(currentSpotPrice, 1)}`
            : "",
        lineColor: "rgba(15, 23, 42, 0.65)",
      },
      volumePercentageLabel: {
        enabled: isVolumeView,
        labelOffset: 8,
      },
    },
    scales: {
      x: {
        type: "linear",
        stacked: showOiOverlay,
        grid: {
          color: "rgba(148, 163, 184, 0.14)",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#5b6474",
          stepSize: strikeStep,
          autoSkip: false,
          padding: 6,
          maxRotation: 45,
          minRotation: 45,
          callback: (value) => formatNumber(value, 0),
        },
      },
      y: {
        stacked: showOiOverlay,
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#5b6474",
          callback: (value) => formatAxisValue(value),
        },
        title: {
          display: true,
          text: isOptionOiChangeView
            ? "Call / Put OI Change"
            : isVolumeView
              ? "Call / Put Volume"
              : "Call / Put OI",
          color: "#4b5563",
          font: {
            size: 13,
            weight: "600",
          },
        },
      },
    },
  };

  return (
    <div className="oi-visual-page">
      <div className="oi-visual-shell">
        <header className="header">
          <nav className="nav-container">
            <div className="header-flex">
              <div className="left-nav">
                <div className="logo-container">
                  <img
                    src="/assets/images/noptionwave-logo-1.png"
                    className="header-logo"
                    alt="noptionwave logo"
                  />
                  <select
                    name="indexFilter"
                    className="index-filter"
                    value={indexFilterValueChanges}
                    onChange={(event) => setIndexFilterValueChangeFilter(event.target.value)}
                    style={{ cursor: "pointer" }}
                    aria-label="Index selector"
                  >
                    {INDEX_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {leftHeaderData.map((item, index) => (
                  <div className="header-item" key={`${item.label1 || "signal"}-${index}`}>
                    {(item.label1 != null || item.value1 != null) && (
                      <span className={`header-line ${item.highlight ? "blink-highlight" : ""}`}>
                        {item.label1 != null && item.label1 !== "" && (
                          <>
                            {item.label1}
                            {item.value1 != null && " : "}
                          </>
                        )}
                        {item.value1 != null && item.value1}
                      </span>
                    )}

                    {(item.label2 != null || item.value2 != null) && (
                      <span className={`header-line ${item.highlight2 ? "blink-highlight" : ""}`}>
                        {item.label2 != null && item.label2 !== "" && (
                          <>
                            {item.label2}
                            {item.value2 != null && " : "}
                          </>
                        )}
                        {item.value2 != null && item.value2}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {rightHeaderData ? (
                <div className="right-nav">
                  <span className="header-line">
                    {rightHeaderData.label1} : {rightHeaderData.value1}
                  </span>
                  <span className="header-line">
                    {rightHeaderData.label2} : {rightHeaderData.value2}
                  </span>
                </div>
              ) : null}
            </div>
          </nav>
        </header>

        <div className="oi-visual-tab-container">
          <div className="oi-visual-tabs" role="tablist" aria-label="OI analysis views">
            <button
              type="button"
              className={`oi-visual-tab ${isOptionOiView ? "is-active" : ""}`}
              onClick={() => setActiveView("optionOi")}
            >
              OPTION OI
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isOptionOiChangeView ? "is-active" : ""}`}
              onClick={() => setActiveView("optionOiChange")}
            >
              OPTION OICHG
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isVolumeView ? "is-active" : ""}`}
              onClick={() => setActiveView("volume")}
            >
              VOLUME
            </button>
            <button
              type="button"
              className={`oi-visual-tab ${isLtpAnalysisView ? "is-active" : ""}`}
              onClick={() => setActiveView("ltpAnalysis")}
            >
              LTP ANALYSIS
            </button>
          </div>
        </div>

        <section className={isLtpAnalysisView ? "oi-ltp-layout" : "oi-visual-card"}>
          {!isLtpAnalysisView ? (
            <div className="oi-visual-card-head">
              <div className="oi-visual-card-title-group">
                <h2>{cardTitle}</h2>
                <button
                  type="button"
                  className="oi-visual-link-button"
                  onClick={() => setShowGuide((currentValue) => !currentValue)}
                >
                  {/* How to read this? */}
                </button>
              </div>

              <div className="oi-visual-card-actions">
                <div className="oi-visual-card-stat">
                  <span>PCR</span>
                  <strong>{formatRatio(activePcr)}</strong>
                </div>

                <div className="oi-visual-filter-group">
                  <span>Range</span>
                  <select
                    className="oi-visual-select"
                    value={selectedStrikeFilter}
                    onChange={(event) => setSelectedStrikeFilter(event.target.value)}
                    aria-label="Visible strike range"
                  >
                    {RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isOptionOiView ? (
                  <label className="oi-visual-toggle">
                    <input
                      type="checkbox"
                      checked={showOiChange}
                      onChange={(event) => setShowOiChange(event.target.checked)}
                    />
                    <span className="oi-visual-toggle-slider" />
                    <span className="oi-visual-toggle-label">Show OI</span>
                    <span className="oi-visual-badge">New</span>
                  </label>
                ) : null}
              </div>
            </div>
          ) : null}

          {showGuide ? <div className="oi-visual-guide">{helperText}</div> : null}

          {isLtpAnalysisView ? (
            ltpAnalysisRows.length > 0 ? (
              <div className="oi-ltp-table-wrap">
                <table className="responsive-table table-bordered text-center sticky-header oi-ltp-table">
                  <thead>
                    <tr>

                      <th className="oi-ltp-call-head">ATH</th>
                      <th className="oi-ltp-call-head">ATL</th>
                      <th className="oi-ltp-call-head">DOWN (%)</th>
                      <th className="oi-ltp-call-head">LTP</th>
                      <th className="oi-ltp-index-head">
                        <select
                          className="oi-ltp-range-select"
                          value={selectedStrikeFilter}
                          onChange={(event) => setSelectedStrikeFilter(event.target.value)}
                          aria-label="Visible strike range"
                        >
                          {RANGE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </th>
                      <th className="oi-ltp-put-head">LTP</th>
                      <th className="oi-ltp-put-head">DOWN (%)</th>
                      <th className="oi-ltp-put-head">ATL</th>
                      <th className="oi-ltp-put-head">ATH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ltpAnalysisRows.map((row, rowIndex) => {
                      const nextRow = ltpAnalysisRows[rowIndex + 1];
                      const isCallNearMaxLow = isPointDifferenceWithinLimit(
                        row.callMaxLow,
                        row.callLtp
                      );
                      const isPutNearMaxLow = isPointDifferenceWithinLimit(
                        row.putMaxLow,
                        row.putLtp
                      );
                      const callRangeTierClass = getLtpRangeTierClass(row.callRangePercentage);
                      const putRangeTierClass = getLtpRangeTierClass(row.putRangePercentage);
                      const isSpotBetween =
                        nextRow &&
                        currentSpotPrice != null &&
                        currentSpotPrice > Math.min(row.strikePrice, nextRow.strikePrice) &&
                        currentSpotPrice < Math.max(row.strikePrice, nextRow.strikePrice);

                      return (
                        <React.Fragment key={`ltp-analysis-${row.strikePrice}`}>
                          {/* <tr className={row.strikePrice === ltpClosestStrike ? "is-active-strike" : ""}> */}
                          <tr>

                            <td>{formatPrice(row.callMaxHigh, 2)}</td>
                            <td className={isCallNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.callMaxLow, 2)}
                            </td>
                            <td className={callRangeTierClass}>
                              {formatPercentValue(row.callRangePercentage, 2)}
                            </td>
                            <td className={isCallNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.callLtp, 2)}
                            </td>
                            <td className="oi-ltp-strike-cell">{formatNumber(row.strikePrice, 0)}</td>
                            <td className={isPutNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.putLtp, 2)}
                            </td>
                            <td className={putRangeTierClass}>
                              {formatPercentValue(row.putRangePercentage, 2)}
                            </td>

                            <td className={isPutNearMaxLow ? "oi-ltp-near-max-low" : ""}>
                              {formatPrice(row.putMaxLow, 2)}
                            </td>
                            <td>{formatPrice(row.putMaxHigh, 2)}</td>
                          </tr>

                          {isSpotBetween ? (
                            <tr className="oi-ltp-spot-row">
                              <td colSpan="9">
                                SPOT - {formatPrice(currentSpotPrice, 2)}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="oi-visual-empty-state">
                Waiting for live option chain data...
              </div>
            )
          ) : chartRows.length > 0 ? (
            <>
              <div className="oi-visual-chart-panel">
                <div className="oi-visual-chart-surface" style={{ width: chartSurfaceWidth }}>
                  <Bar
                    key={`${indexFilterValueChanges}-${selectedStrikeFilter}-${activeView}-${showOiOverlay}`}
                    data={chartData}
                    options={chartOptions}
                    plugins={[spotMarkerPlugin, volumePercentageLabelPlugin]}
                  />
                </div>
              </div>

              {isOptionOiView || isVolumeView ? (
                <div className="oi-visual-summary">
                  <div className={`oi-visual-summary-item ${isVolumeView ? "is-volume-detail" : ""}`}>
                    <span className="oi-visual-summary-label">{visiblePutLabel}</span>
                    {isVolumeView ? (
                      <div className="oi-visual-summary-value is-volume-detail">
                        <span>({formatNumber(visiblePutValue, 0)})</span>
                        <strong>{formatCompactIndianValue(visiblePutValue)}</strong>
                      </div>
                    ) : (
                      <strong>{formatCompactIndianValue(visiblePutValue)}</strong>
                    )}
                  </div>

                  <div className={`oi-visual-summary-item ${isVolumeView ? "is-volume-detail" : ""}`}>
                    <span className="oi-visual-summary-label">{visibleCallLabel}</span>
                    {isVolumeView ? (
                      <div className="oi-visual-summary-value is-volume-detail">
                        <span>({formatNumber(visibleCallValue, 0)})</span>
                        <strong>{formatCompactIndianValue(visibleCallValue)}</strong>
                      </div>
                    ) : (
                      <strong>{formatCompactIndianValue(visibleCallValue)}</strong>
                    )}
                  </div>

                  <div className="oi-visual-summary-item is-pcr">
                    <span className="oi-visual-summary-label">PCR</span>
                    <strong>{formatRatio(visiblePcr)}</strong>
                  </div>
                </div>
              ) : null}

              {isVolumeView ? (
                <section className="oi-minute-volume-section">
                  <div className="oi-minute-volume-grid">
                    {minuteVolumePanels.map((panel) => (
                      <article
                        key={panel.key}
                        className={`oi-minute-volume-card ${panel.cardClassName}`}
                      >
                        <div className="oi-minute-volume-card-head">{panel.title}</div>

                        <div className="oi-minute-volume-table-wrap">
                          <table className="oi-minute-volume-table">
                            <thead>
                              <tr>
                                <th>TIME</th>
                                <th>{panel.barLabel}</th>
                              </tr>
                            </thead>

                            <tbody>
                              {panel.data.rows.length > 0 ? (
                                panel.data.rows.map((row) => {
                                  const barValue = row.barVolume;
                                  const barWidth = panel.data.maxVolume > 0
                                    ? `${(toNumber(barValue) / panel.data.maxVolume) * 100}%`
                                    : "0%";

                                  return (
                                    <tr key={`${panel.key}-${row.rank}-${row.minuteLabel}`}>
                                      <td className="oi-minute-volume-minute-cell">{row.minuteLabel}</td>
                                      <td className="oi-minute-volume-bar-cell">
                                        <div className="oi-minute-volume-bar-row">
                                          <div className="oi-minute-volume-bar-track">
                                            <div
                                              className="oi-minute-volume-bar-fill"
                                              style={{ width: barWidth }}
                                            />
                                          </div>
                                          <span className="oi-minute-volume-bar-text">
                                            {formatNumber(barValue, 0)}
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td className="oi-minute-volume-empty" colSpan="2">
                                    Waiting for {panel.title.toLowerCase()} data...
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="oi-minute-volume-card-footer">
                          <span>{panel.totalLabel}</span>
                          <strong>{formatNumber(panel.data.totalVolume, 0)}</strong>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : (
            <div className="oi-visual-empty-state">
              Waiting for live option chain data...
            </div>
          )}
        </section>

        <div className="fixed-footer">
          <span>noptionwave © 2026</span>
        </div>
      </div>
    </div>
  );
}
